import type { AgentAccent, Citation } from '@ralph/contracts'
import { ArrowUp, FlaskConical, Square, TriangleAlert } from 'lucide-react'
import { type ReactNode, useRef, useState } from 'react'
import { AssistantMark } from '@/brand/BrandMarks.tsx'
import { Answer } from '@/components/chat/Answer.tsx'
import { ApprovalCard } from '@/components/chat/ApprovalCard.tsx'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { Note } from '@/components/marketplace/maker/BuilderShell.tsx'
import { Button, Spinner } from '@/components/ui/primitives.tsx'
import { useResolveApproval } from '@/hooks/use-api.ts'
import { useStartPreview } from '@/hooks/use-maker.ts'
import { useRunStream } from '@/hooks/use-run-stream.ts'
import { describeIssue, useT } from '@/i18n/index.ts'

type Turn = { id: string; role: 'user' | 'assistant'; content: string; citations: Citation[] }

/**
 * The draft, running for real.
 *
 * The preview is a real conversation on the real pipeline — same rules, same citations,
 * same approval cards — which is the only way it can be evidence about what people will get
 * (AGM-029). Two things differ and both are server-side: the run is marked as a preview so
 * it stays out of consumer analytics, and write-capable capabilities do not fire. The
 * caption says so on the pane rather than in a tooltip, because a maker who believes an
 * action really happened here would draw exactly the wrong conclusion from it.
 *
 * No model or depth control appears — the consumer surface has none either (AGM-009), and a
 * preview that let the maker tune something a reader cannot would be lying about what the
 * reader will experience.
 *
 * Visually it is the conversation screen at a smaller scale, and that is the point: an
 * iridescent bubble for the maker's own question, the assistant's orb on the byline above the
 * answer, and a composer with the same lit edge. A preview that looked like a form field
 * would be evidence about the form rather than about the conversation.
 */
export const PreviewPane = ({
  agentId,
  icon,
  displayName,
  accent,
  starters,
  dirty,
  canPreview
}: {
  agentId: string
  icon: string
  displayName: string
  accent: AgentAccent
  starters: string[]
  dirty: boolean
  canPreview: boolean
}) => {
  // In the body, never at module scope (PRD-27 F-3).
  const messages = useT()
  const strings = messages.marketplace.maker.preview
  const start = useStartPreview(agentId)
  const run = useRunStream()
  const resolveApproval = useResolveApproval()
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState('')
  const composerRef = useRef<HTMLTextAreaElement>(null)

  const submit = async () => {
    const content = draft.trim()
    if (content === '' || conversationId === null || run.isStreaming) return

    // The finished answer of the PREVIOUS run lives in the stream state until the next send
    // clears it, so this is the moment it becomes history. Reading it here rather than from
    // an effect avoids racing the reducer's last dispatch against the promise resolving.
    const carried: Turn[] =
      run.state.text.trim() === ''
        ? []
        : [{ id: `a-${turns.length}`, role: 'assistant', content: run.state.text, citations: run.state.citations }]

    setDraft('')
    setTurns((current) => [...current, ...carried, { id: `u-${current.length}`, role: 'user', content, citations: [] }])

    const result = await run.send(conversationId, content)
    // A send the server never accepted leaves nothing on screen to retry from; hand the
    // question back rather than eating what the maker just typed.
    if (!result.accepted) setDraft((current) => (current === '' ? content : current))
  }

  const decide = async (decision: {
    decision: 'approved' | 'rejected' | 'edited_and_approved'
    digest: string
    editedArguments?: Record<string, unknown>
    note?: string
  }) => {
    const approval = run.state.approval
    if (!approval) return
    const result = await resolveApproval.mutateAsync({ id: approval.approvalId, ...decision })
    await run.reattach(result.runId)
  }

  const failure = run.state.error
  const quotaHit = failure?.code.includes('quota') ?? false
  // A failed run takes the same two steps `describeApiError` takes for a failed request
  // (PRD-27 D-4): the key wins when this build knows it, and the server's English is kept as
  // the fallback for one it does not — which is the deploy-order guarantee, not a defect. It
  // cannot go through `describeApiError` itself, because a stream failure never was an
  // `ApiRequestError`: the SSE event carries the key and its params directly.
  const failureSentence = failure === null ? '' : (describeIssue(messages, failure) ?? failure.message)

  return (
    <div className="card flex h-full min-h-[26rem] flex-col overflow-clip rounded-panel">
      <header className="flex items-center gap-2.5 border-b border-rule px-3.5 py-3">
        <AgentAvatar icon={icon} displayName={displayName} accent={accent} size="sm" />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-sans text-[14px] font-semibold tracking-[-0.018em]">
            {displayName || strings.title}
          </h2>
          {/* The one thing about this pane that is not the real conversation, said on the pane
              itself: an action a maker believes really fired is the wrong lesson to take away. */}
          <p className="readout mt-0.5 flex items-center gap-1.5 text-fg-subtle">
            <FlaskConical size={10} strokeWidth={2.4} aria-hidden className="shrink-0" />
            <span className="truncate">{strings.caption}</span>
          </p>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3.5 py-3.5">
        <p className="text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.captionHint}</p>

        {conversationId === null ? (
          <div className="space-y-2.5">
            <Button
              variant="primary"
              disabled={!canPreview || start.isPending}
              icon={start.isPending ? <Spinner size={13} /> : undefined}
              onClick={() =>
                start.mutate(undefined, { onSuccess: (result) => setConversationId(result.conversationId) })
              }
            >
              {start.isPending ? strings.starting : strings.start}
            </Button>
            {start.isError && (
              <Note tone="warn" icon={<TriangleAlert size={13} strokeWidth={2.1} />}>
                {strings.startFailed}
              </Note>
            )}
          </div>
        ) : (
          <>
            {turns.length === 0 && run.state.text === '' && !run.isStreaming && (
              <p className="text-[13px] text-fg-subtle">{strings.empty}</p>
            )}

            <div className="space-y-4">
              {turns.map((turn) =>
                turn.role === 'user' ? (
                  <Question key={turn.id}>{turn.content}</Question>
                ) : (
                  <Reply key={turn.id} agentName={displayName}>
                    <Answer content={turn.content} citations={turn.citations} />
                  </Reply>
                )
              )}
            </div>

            {/*
              The answer is announced once, when it is finished.
              A live region fed token by token turns a streaming answer into a stutter of
              partial sentences in a screen reader (AGM-124), so the streaming copy is
              hidden from assistive technology and the completed one is what gets read.
            */}
            <div aria-live="polite" aria-atomic="true">
              {!run.isStreaming && run.state.text !== '' && (
                <Reply agentName={displayName}>
                  <Answer content={run.state.text} citations={run.state.citations} />
                </Reply>
              )}
            </div>

            {run.isStreaming && (
              <div aria-hidden>
                {run.state.text === '' ? (
                  <p className="readout flex items-center gap-2 text-fg-subtle">
                    <Spinner size={12} />
                    {run.state.thinking !== '' ? strings.thinking : (run.state.nodes.at(-1)?.label ?? strings.thinking)}
                  </p>
                ) : (
                  <Reply agentName={displayName} live>
                    <Answer content={run.state.text} citations={run.state.citations} streaming />
                  </Reply>
                )}
              </div>
            )}

            {run.state.approval && (
              <div className="space-y-2">
                <Note tone="info" icon={<FlaskConical size={13} strokeWidth={2.1} />}>
                  {strings.simulated}
                </Note>
                <ApprovalCard approval={run.state.approval} onDecide={decide} busy={resolveApproval.isPending} />
              </div>
            )}

            {failure && (
              <Note tone="warn" icon={<TriangleAlert size={13} strokeWidth={2.1} />}>
                {quotaHit ? strings.quota : `${strings.failedRun} ${failureSentence}`}
              </Note>
            )}
          </>
        )}
      </div>

      {conversationId !== null && (
        <div className="border-t border-rule px-3.5 py-3">
          {dirty && <p className="readout mb-2 text-fg-subtle">{strings.saveFirst}</p>}

          {starters.length > 0 && turns.length === 0 && (
            <ul className="mb-2.5 flex flex-wrap gap-1.5">
              {starters
                .filter((starter) => starter.trim() !== '')
                .map((starter) => (
                  <li key={starter}>
                    {/* Fills the box, never sends it — the same rule the listing page keeps
                        (AGM-005), and the reason the focus jumps to the composer after. */}
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(starter)
                        composerRef.current?.focus()
                      }}
                      className="rounded-full border border-rule bg-card-solid px-2.5 py-1 text-left text-[12px] text-fg-muted transition-[color,border-color,transform] duration-200 hover:-translate-y-px hover:border-rule-strong hover:text-fg"
                    >
                      {starter}
                    </button>
                  </li>
                ))}
            </ul>
          )}

          {/*
            The composer's lit edge: a gradient hairline drawn a pixel outside an opaque inner
            field. It is the same ornament as the conversation composer, at this scale, and it
            is the third and last thing in the product allowed to wear the gradient.
          */}
          <div className="group relative">
            <div
              aria-hidden
              className="absolute -inset-px rounded-[14px] bg-[image:var(--irid)] opacity-70 transition-opacity duration-200 group-focus-within:opacity-100"
            />
            <div className="relative flex items-end gap-2 rounded-tile bg-card-solid p-1.5">
              <textarea
                ref={composerRef}
                value={draft}
                rows={1}
                placeholder={strings.placeholder}
                aria-label={strings.placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void submit()
                  }
                }}
                className="max-h-32 min-h-[30px] flex-1 resize-none rounded-[9px] border-0 bg-transparent px-2 py-1.5 text-[13.5px] leading-[1.5] text-fg placeholder:text-fg-subtle"
              />
              {run.isStreaming ? (
                <Button icon={<Square size={13} strokeWidth={2.1} aria-hidden />} onClick={run.cancel}>
                  {strings.stop}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  icon={<ArrowUp size={14} strokeWidth={2.4} aria-hidden />}
                  disabled={draft.trim() === ''}
                  aria-label={strings.send}
                  onClick={() => void submit()}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** The maker's own question, in the one bubble shape the product gives a person's own words. */
const Question = ({ children }: { children: ReactNode }) => (
  <div className="flex justify-end">
    <div className="max-w-[85%] whitespace-pre-wrap rounded-[16px] rounded-br-[6px] bg-[image:var(--irid)] px-3.5 py-2 text-[13.5px] leading-[1.5] text-white shadow-[var(--irid-glow)]">
      {children}
    </div>
  </div>
)

/**
 * An answer with its byline.
 *
 * The orb marks the model itself, which is exactly what is speaking here; the agent's name
 * beside it says which configuration of it. `live` breathes the byline while the answer is
 * still arriving, so the pane has the same "something is happening" signal as the real
 * conversation without a second spinner.
 */
const Reply = ({ agentName, live, children }: { agentName: string; live?: boolean; children: ReactNode }) => (
  <div>
    <div className="mb-2 flex items-center gap-2">
      <AssistantMark size={18} className={live ? 'pulse-signal' : undefined} />
      {agentName !== '' && <span className="eyebrow truncate text-fg-muted">{agentName}</span>}
      <span aria-hidden className="h-px flex-1 bg-rule" />
    </div>
    {children}
  </div>
)
